/**
 * Константы для query keys TanStack Query
 * Используются для инвалидации queries после мутаций
 */

/**
 * Константы для ключей зависимостей данных (типы сущностей для инвалидации)
 */
export const DATA_REFRESH_KEYS = {
  INCOMES: 'incomes',
  SPENDINGS: 'spendings',
  TACTICS: 'tactics',
  PLAN: 'plan',
  GOALS: 'goals',
  INSURANCES: 'insurances',
  INVESTS: 'invests',
  REPOS: 'repos',
  LOGS: 'logs',
  ROBOADVISOR: 'roboadvisor',
  OBLIGATIONS: 'obligations',
  ACCOUNTS: 'accounts',
} as const;

export type DataRefreshKey = (typeof DATA_REFRESH_KEYS)[keyof typeof DATA_REFRESH_KEYS];

export const QUERY_KEYS = {
  // План
  plan: {
    /** Базовый ключ для инвалидации запросов плана */
    base: (): unknown[] => ['plan'],
    /** План пользователя (user-plan/index) */
    all: (clientId: unknown): unknown[] => ['plan', clientId],
  },

  // Доходы
  incomes: {
    /** Базовый ключ для инвалидации всех запросов доходов (все валюты) */
    base: (): unknown[] => ['incomes'],
    all: (clientId: unknown, currencyId: unknown): unknown[] => ['incomes', clientId, currencyId],
    list: (clientId: unknown, currencyId: unknown, filters: unknown): unknown[] => [
      'incomes',
      clientId,
      currencyId,
      filters,
    ],
  },

  // Расходы
  spendings: {
    /** Базовый ключ для инвалидации всех запросов расходов (все валюты) */
    base: (): unknown[] => ['spendings'],
    all: (clientId: unknown, currencyId: unknown): unknown[] => ['spendings', clientId, currencyId],
    list: (clientId: unknown, currencyId: unknown, filters: unknown): unknown[] => [
      'spendings',
      clientId,
      currencyId,
      filters,
    ],
  },

  // Тактика
  tactics: {
    /** Базовый ключ для инвалидации всех запросов тактики (все валюты, все периоды) */
    base: (): unknown[] => ['tactics'],
    byDateRange: (
      clientId: unknown,
      currencyId: unknown,
      start: unknown,
      end: unknown
    ): unknown[] => ['tactics', 'spendings', clientId, currencyId, start, end],
    firstTransaction: (clientId: unknown, currencyId: unknown): unknown[] => [
      'tactics',
      'firstTransaction',
      clientId,
      currencyId,
    ],
    factIncome: (clientId: unknown, currencyId: unknown): unknown[] => [
      'tactics',
      'factIncome',
      clientId,
      currencyId,
    ], // Фактические доходы для плана
  },

  // Цели
  goals: {
    /** Базовый ключ для инвалидации всех запросов целей (все валюты) */
    base: (): unknown[] => ['goals'],
    all: (clientId: unknown, currencyId: unknown): unknown[] => ['goals', clientId, currencyId],
    /** Цели с графиком для страницы /cabinet/plan/goals (отдельно от salary-plan goals.all) */
    withChart: (clientId: unknown, currencyId: unknown): unknown[] => [
      'goals',
      'withChart',
      clientId,
      currencyId,
    ],
    detail: (id: unknown): unknown[] => ['goals', 'detail', id],
    /** Цели для страницы планов (SalaryPlan) */
    plan: (clientId: unknown): unknown[] => ['goals', 'plan', clientId],
    /** История портфеля цели (привязанные активы) */
    portfolioHistory: (goalId: unknown): unknown[] => ['goals', 'portfolioHistory', goalId],
    /** Привязанные к цели активы (модалка закрытия цели) */
    linkedActives: (goalId: unknown, userId: unknown): unknown[] => [
      'goals',
      'linkedActives',
      goalId,
      userId,
    ],
  },

  // Элементы целей (привязка активов и счетов к целям)
  goalItems: {
    /** Базовый ключ для инвалидации всех запросов элементов целей (в namespace goals) */
    base: (): unknown[] => ['goals', 'items'],
    /** Обязательные платежи целей для дашборда */
    dashboardMustPayments: (clientId: unknown): unknown[] => [
      'goals',
      'items',
      'dashboardMustPayments',
      clientId,
    ],
    /** Подключённые элементы для цели (is_connected = 1) */
    connectedItems: (goalId: unknown): unknown[] => ['goals', 'items', 'connected', goalId],
    /** Все элементы для цели */
    allItems: (goalId: unknown): unknown[] => ['goals', 'items', 'all', goalId],
    /** Все элементы по всем целям пользователя (exceptItems) */
    exceptItems: (clientId: unknown): unknown[] => ['goals', 'items', 'except', clientId],
    /** Платежи по цели */
    payments: (goalId: unknown): unknown[] => ['goals', 'items', 'payments', goalId],
  },

  // Страхования
  insurances: {
    /** Базовый ключ для инвалидации всех запросов страхований (все валюты, архив и не архив) */
    base: (): unknown[] => ['insurances'],
    all: (clientId: unknown, currencyId: unknown, isArchive: unknown): unknown[] => [
      'insurances',
      clientId,
      currencyId,
      isArchive,
    ],
    detail: (id: unknown): unknown[] => ['insurances', 'detail', id],
  },

  // Платежи по активу (active-payment: обязательства и страхования)
  activePayments: {
    base: (): unknown[] => ['activePayments'],
    /** Список платежей по item (item_type + item_id: обязательство или страховой актив) */
    byItem: (clientId: unknown, itemId: unknown): unknown[] => [
      'activePayments',
      'byItem',
      clientId,
      itemId,
    ],
  },

  // Дашборд
  dashboard: {
    /** @deprecated Алиас invests.cabinetInvestGridActives (тот же массив ключа) */
    portfolioPiesBase: (): unknown[] => ['invests', 'cabinetInvestGrid'],
    portfolioPies: (clientId: unknown, currencyId: unknown): unknown[] => [
      'invests',
      'cabinetInvestGrid',
      clientId,
      currencyId,
      'all',
      'open_invest_traded',
    ],
  },

  // Инвестиции
  invests: {
    /** Базовый ключ для инвалидации всех запросов инвестиций (без id, exact: false) */
    base: (): unknown[] => ['invests'],
    all: (clientId: unknown, requestClientId: unknown, currencyId: unknown): unknown[] => [
      'invests',
      clientId,
      requestClientId,
      currencyId,
    ],
    /** Активы для привязки к целям — invest-grid-optimized-index (`open_invest_traded_currency`) */
    forGoalBinding: (clientId: unknown, currencyId: unknown): unknown[] => [
      'invests',
      'forGoalBinding',
      clientId,
      currencyId,
    ],
    /** Префикс-ключ для инвалидации всех forGoalBinding вариантов */
    forGoalBindingBase: (): unknown[] => ['invests', 'forGoalBinding'],
    /**
     * Case pie/tree + dashboard «Портфель»: invest-grid как /invest (`open_invest_traded`),
     * валюта кабинета (`interfaceComponents.currency` / валюта клиента).
     */
    cabinetInvestGridActives: (
      clientId: unknown,
      currencyId: unknown,
      requestClientId: unknown = null
    ): unknown[] => [
      'invests',
      'cabinetInvestGrid',
      clientId,
      currencyId,
      requestClientId ?? 'all',
      'open_invest_traded',
    ],
    cabinetInvestGridActivesBase: (): unknown[] => ['invests', 'cabinetInvestGrid'],
    /** Средняя стоимость активов для dashboard pie (web worker, общий префикс с cabinetInvestGrid). */
    cabinetInvestGridAverageActivesCost: (
      clientId: unknown,
      currencyId: unknown,
      requestClientId: unknown,
      groupTypesCount: unknown,
      portfolioDataUpdatedAt: unknown,
      accountsCount: unknown
    ): unknown[] => [
      ...QUERY_KEYS.invests.cabinetInvestGridActives(clientId, currencyId, requestClientId),
      groupTypesCount,
      portfolioDataUpdatedAt ?? 0,
      accountsCount ?? 0,
      'averageActivesCost',
    ],
    /** @deprecated Используйте cabinetInvestGridActives */
    caseInvestActives: (
      clientId: unknown,
      currencyId: unknown,
      requestClientId: unknown
    ): unknown[] =>
      QUERY_KEYS.invests.cabinetInvestGridActives(clientId, currencyId, requestClientId),
    caseInvestActivesBase: (): unknown[] => QUERY_KEYS.invests.cabinetInvestGridActivesBase(),
    /** График портфеля (CaseTreeChart) — invest-grid-optimized-index */
    portfolioTreeChart: (clientId: unknown, currencyId: unknown): unknown[] => [
      'invests',
      'portfolioTreeChart',
      clientId,
      currencyId,
    ],
    /** Префикс-ключ для инвалидации всех portfolioTreeChart вариантов */
    portfolioTreeChartBase: (): unknown[] => ['invests', 'portfolioTreeChart'],
    /** @deprecated Используйте caseInvestActives — общий запрос с /invest */
    casePiesActives: (clientId: unknown, currencyId: unknown): unknown[] => [
      'invests',
      'casePiesActives',
      clientId,
      currencyId,
    ],
    /** Префикс-ключ для инвалидации всех casePiesActives вариантов */
    casePiesActivesBase: (): unknown[] => ['invests', 'casePiesActives'],
    /** Блок статистики (CaseNumbers) — invest-grid-optimized-index (`case_numbers`) */
    caseNumbers: (clientId: unknown, currencyId: unknown): unknown[] => [
      'invests',
      'caseNumbers',
      clientId,
      currencyId,
    ],
    /** Префикс-ключ для инвалидации всех caseNumbers вариантов */
    caseNumbersBase: (): unknown[] => ['invests', 'caseNumbers'],
    /** Служебный ключ для частичного апдейта активов инвестиций по массиву id */
    partialByIds: (
      clientId: unknown,
      requestClientId: unknown,
      currencyId: unknown,
      idsHash: unknown
    ): unknown[] => ['invests', 'partialByIds', clientId, requestClientId, currencyId, idsHash],
  },

  // Архивные инвестиции (отдельный ключ)
  archive: {
    /** Базовый ключ: без аргументов — все архивы; с (clientId, requestClientId) — по пользователю */
    base: (clientId?: unknown, requestClientId?: unknown): unknown[] =>
      clientId != null && requestClientId != null
        ? ['archive', clientId, requestClientId]
        : ['archive'],
    all: (
      clientId: unknown,
      requestClientId: unknown,
      currencyId: unknown,
      search: unknown,
      currentPage: unknown,
      perPage: unknown,
      filterAccountIds: unknown
    ): unknown[] => [
      'archive',
      clientId,
      requestClientId,
      currencyId,
      search,
      currentPage,
      perPage,
      filterAccountIds,
    ],
  },

  // REPO
  /** Сделка по купону (форма CouponDeal) */
  couponDeals: {
    base: (): unknown[] => ['couponDeals'],
    detail: (clientId: unknown, dealId: unknown, typeId?: unknown): unknown[] => [
      'couponDeals',
      clientId,
      'detail',
      dealId,
      typeId ?? null,
    ],
  },

  repos: {
    /** Базовый ключ: без аргументов — все репо; с requestClientId — по пользователю */
    base: (requestClientId?: unknown): unknown[] =>
      requestClientId != null ? ['repos', requestClientId] : ['repos'],
    all: (requestClientId: unknown, closed?: unknown): unknown[] => [
      'repos',
      requestClientId,
      closed,
    ],
    /** Список РЕПО: один index с `user_id` = кабинет; фильтр по портфелю на фронте. */
    byCabinet: (cabinetUserId: unknown, closed?: unknown): unknown[] => [
      'repos',
      'cabinet',
      cabinetUserId,
      closed,
    ],
    /** Один репо по id (для формы редактирования) */
    detail: (clientId: unknown, repoId: unknown): unknown[] => [
      'repos',
      clientId,
      'detail',
      repoId,
    ],
  },

  // Комиссии по сделке (active-trade-commission, список в форме покупки/продажи актива)
  tradeCommissions: {
    base: (): unknown[] => ['tradeCommissions'],
    list: (clientId: unknown, tradeId: unknown): unknown[] => [
      'tradeCommissions',
      clientId,
      tradeId,
    ],
  },

  // Комиссии по переводу (transfer-commission, список в форме перевода)
  transferCommissions: {
    base: (): unknown[] => ['transferCommissions'],
    list: (clientId: unknown, transferId: unknown): unknown[] => [
      'transferCommissions',
      clientId,
      transferId,
    ],
  },

  // Ticker hub link (external_id + catalog_source_id -> ticker_hub_id)
  tickerHubLink: (externalId: unknown, catalogSourceId: unknown): unknown[] => [
    'tickerHubLink',
    externalId,
    catalogSourceId,
  ],

  // Логи операций
  logs: {
    base: (): unknown[] => ['logs'],
    all: (clientId: unknown): unknown[] => ['logs', clientId],
  },

  // Excess (профиль / избыточные данные)
  excess: {
    base: (): unknown[] => ['excess'],
    all: (clientId: unknown): unknown[] => ['excess', clientId],
  },

  // Интеграции (токены подключений брокеров/сервисов)
  integrationTokens: {
    base: (): unknown[] => ['integrationTokens'],
    all: (clientId: unknown): unknown[] => ['integrationTokens', clientId],
  },

  // Банки (список банков для выбора типа счёта «Банковский»)
  banks: {
    base: (): unknown[] => ['banks'],
    all: (clientId: unknown): unknown[] => ['banks', clientId],
  },

  // Брокеры (список брокеров для выбора при подключении робоэдвайзера и типа счёта «Брокерский»)
  brokers: {
    base: (): unknown[] => ['brokers'],
    all: (clientId: unknown): unknown[] => ['brokers', clientId],
  },

  // Робоэдвайзер
  roboadvisor: {
    all: (clientId: unknown): unknown[] => ['roboadvisor', 'connection', clientId],
    detail: (roboId: unknown, clientId: unknown): unknown[] => [
      'roboadvisor',
      'detail',
      roboId,
      clientId,
    ],
    status: (roboId: unknown): unknown[] => ['roboadvisor', 'status', roboId],
    notifications: (clientId: unknown, page: unknown, take: unknown): unknown[] => [
      'roboadvisor',
      'notifications',
      clientId,
      page,
      take,
    ],
    /** Транзакции робоэдвайзера; без page/take — префикс для инвалидации всех вариантов пагинации */
    trades: (roboId: unknown, clientId: unknown, page?: unknown, take?: unknown): unknown[] =>
      page !== undefined && take !== undefined
        ? ['roboadvisor', 'trades', roboId, clientId, page, take]
        : ['roboadvisor', 'trades', roboId, clientId],
    /** Доступная сумма для вывода (GET integration/robo-balance) */
    balance: (roboId: unknown, currency: unknown): unknown[] => [
      'roboadvisor',
      'balance',
      roboId,
      currency,
    ],
    /** Паспортные данные для шага договора (GET integration/user-passport) */
    userPassport: (): unknown[] => ['roboadvisor', 'userPassport'],
    /** Список целей для шага выбора цели в визарде (active-goal/index) */
    wizardGoals: (clientId: unknown): unknown[] => ['roboadvisor', 'wizardGoals', clientId],
    /** Активы для шага подтверждения продажи (active/index с фильтрами) */
    confirmSaleActives: (clientId: unknown, accountId: unknown, currencyId: unknown): unknown[] => [
      'roboadvisor',
      'confirmSaleActives',
      clientId,
      accountId,
      currencyId,
    ],
  },

  // Обязательства
  obligations: {
    /** Базовый ключ для инвалидации всех запросов обязательств (все валюты) */
    base: (): unknown[] => ['obligations'],
    all: (clientId: unknown, currencyId: unknown): unknown[] => [
      'obligations',
      clientId,
      currencyId,
    ],
    /** Список обязательств id+name для селектов (страхования и др.) */
    forSelect: (clientId: unknown, currencyId: unknown): unknown[] => [
      'obligations',
      'forSelect',
      clientId,
      currencyId,
    ],
    detail: (id: unknown): unknown[] => ['obligations', 'detail', id],
    payments: (obligationId: unknown): unknown[] => ['obligations', 'payments', obligationId],
  },

  // Счета
  accounts: {
    /** Базовый ключ для инвалидации всех запросов счетов (видимые и архивные) */
    base: (): unknown[] => ['accounts'],
    all: (clientId: unknown, isArchive: unknown): unknown[] => ['accounts', clientId, isArchive],
    /**
     * user-account/index для селекта в DeleteAccountBlock (Интеграции).
     * Без where(is_visible) — как прежний loadAllAccounts в компоненте.
     */
    integrationsDeleteBlockIndex: (clientId: unknown): unknown[] => [
      'accounts',
      'integrationsDeleteBlockIndex',
      clientId,
    ],
  },

  // Активы на странице Счета/Активы/Обязательства (Properties, текущие и архив)
  properties: {
    /** Базовый ключ для инвалидации всех запросов списка активов (текущие + архив) */
    base: (): unknown[] => ['properties'],
    /** Список активов по clientId, currencyId, typeArchive (false = текущие, true = архив) */
    list: (clientId: unknown, currencyId: unknown, typeArchive: unknown): unknown[] => [
      'properties',
      clientId,
      currencyId,
      typeArchive,
    ],
    /** Список недвижимостей id+name для селектов (страхования: текущие, без продажи) */
    forSelect: (clientId: unknown, currencyId: unknown): unknown[] => [
      'properties',
      'forSelect',
      clientId,
      currencyId,
    ],
  },

  // Точки восстановления (Config / Backup)
  backups: {
    /** Базовый ключ для инвалидации всех запросов списка точек восстановления */
    base: (): unknown[] => ['backups'],
    /** Список точек восстановления по clientId */
    list: (clientId: unknown): unknown[] => ['backups', clientId],
  },

  // Пользовательские коллективные группы (вкладки в архиве инвестиций)
  userCollectiveGroups: {
    base: (): unknown[] => ['userCollectiveGroups'],
    all: (clientId: unknown): unknown[] => ['userCollectiveGroups', clientId],
  },

  // Активы: список для формы конвертации (только с buy_trades, без sell_trades)
  active: {
    base: (): unknown[] => ['active'],
    /** Один актив по id (для формы редактирования PropertyExtendedBlock) */
    detail: (activeId: unknown, clientId: unknown): unknown[] => [
      'active',
      'detail',
      activeId,
      clientId,
    ],
    /** Список активов для выбора «что меняем» в форме конвертации */
    convertFromItems: (clientId: unknown): unknown[] => ['active', 'convertFromItems', clientId],
    /** Поиск активов «на что меняем» (по строке поиска) */
    convertItemToSearch: (clientId: unknown, search: string): unknown[] => [
      'active',
      'convertItemToSearch',
      clientId,
      search,
    ],
    /** Бумаги клиента для формы РЕПО (active/index, PACKAGE_GROUP, tickerHub) */
    repoPaperItems: (clientId: unknown): unknown[] => ['active', 'repoPaperItems', clientId],
    repoPaperItemsByIsin: (clientId: unknown, isinToken: unknown): unknown[] => [
      'active',
      'repoPaperItemsByIsin',
      clientId,
      isinToken,
    ],
    /** Бумаги для сделки по купону (акции + облигации) */
    couponDealPaperItems: (clientId: unknown): unknown[] => [
      'active',
      'couponDealPaperItems',
      clientId,
    ],
    couponDealPaperItemsByIsin: (clientId: unknown, isinToken: unknown): unknown[] => [
      'active',
      'couponDealPaperItemsByIsin',
      clientId,
      isinToken,
    ],
    /** Поиск активов по типу (акции, облигации, ETF, валюты, фьючерсы) для форм Actives */
    catalogSearch: (clientId: unknown, search: string, method: string): unknown[] => [
      'active',
      'catalogSearch',
      clientId,
      search,
      method,
    ],
    /** Поиск активов для формы паёв фонда (PACKAGE_GROUP, buy_trades, без sell_trades, без active_fund_relation) */
    fundItemSearch: (clientId: unknown, activeFundId: unknown, search: string): unknown[] => [
      'active',
      'fundItemSearch',
      clientId,
      activeFundId,
      search,
    ],
    /**
     * Справочник типов процентов для депозита/долга (endpoint: active/deposit-percent-types).
     * Используется для селекта "Тип процента" в формах инвестиций.
     */
    depositPercentTypes: {
      base: (): unknown[] => ['depositPercentTypes'],
      all: (): unknown[] => ['depositPercentTypes', 'all'],
    },
  },

  // Паи фонда (active-fund-item)
  activeFundItems: {
    base: (): unknown[] => ['activeFundItems'],
    /** Список паёв по фонду (active-fund-item/index, active_fund_id) */
    byFund: (clientId: unknown, activeFundId: unknown): unknown[] => [
      'activeFundItems',
      'byFund',
      clientId,
      activeFundId,
    ],
  },

  // Перевод бумаг (модалка «Перевод бумаг»)
  transferTrades: {
    base: (): unknown[] => ['transferTrades'],
    /** Список инструментов для выбора (active/index с buy_trades, без sell_trades) */
    items: (clientId: unknown): unknown[] => ['transferTrades', 'items', clientId],
    /** Точечный список инструментов по ISIN/коду (original_ticker) */
    itemsByIsin: (clientId: unknown, isinToken: unknown): unknown[] => [
      'transferTrades',
      'itemsByIsin',
      clientId,
      isinToken,
    ],
  },

  // Перевод бумаг на API (модалка «Перевод бумаг на API»)
  transferTradesToApi: {
    base: (): unknown[] => ['transferTradesToApi'],
    /** Список активов с buy_trades (from_account is_api=0), без sell_trades */
    items: (clientId: unknown): unknown[] => ['transferTradesToApi', 'items', clientId],
    /** Точечный список активов по ISIN/коду (original_ticker) */
    itemsByIsin: (clientId: unknown, isinToken: unknown): unknown[] => [
      'transferTradesToApi',
      'itemsByIsin',
      clientId,
      isinToken,
    ],
  },

  // Группы активов (страница Инвестиции → Диаграммы: базовые и пользовательские)
  activeGroups: {
    /** Базовый ключ для инвалидации списка групп */
    base: (): unknown[] => ['activeGroups'],
    /** Базовые + пользовательские группы по clientId (active-group/base-groups) */
    baseGroups: (clientId: unknown): unknown[] => ['activeGroups', 'baseGroups', clientId],
    /** Элементы группы (active-group-item/index по group_id) для модалки редактирования */
    items: (groupId: unknown): unknown[] => ['activeGroups', 'items', groupId],
    /** Одна группа с items для модалки редактирования */
    detail: (clientId: unknown, groupId: unknown): unknown[] => [
      'activeGroups',
      'detail',
      clientId,
      groupId,
    ],
  },

  // Подписки (SubscribePage)
  subscribes: {
    base: (): unknown[] => ['subscribes'],
    all: (): unknown[] => ['subscribes'],
  },

  // Credit log (TacticsLog, TransactionChart)
  creditLog: {
    base: (): unknown[] => ['creditLog'],
    byType: (
      type: unknown,
      clientId: unknown,
      startDate: unknown,
      endDate: unknown,
      accountIds: unknown,
      currencyId?: unknown,
      typeIds?: unknown
    ): unknown[] => [
      'creditLog',
      type,
      clientId,
      startDate,
      endDate,
      accountIds,
      currencyId,
      typeIds,
    ],
  },

  // Cabinet value (CaseValue)
  cabinetValue: {
    base: (): unknown[] => ['cabinetValue'],
    value: (params: unknown): unknown[] => ['cabinetValue', 'value', params],
  },

  // Типы обязательств (ObligationExtendedBlock)
  obligationTypes: {
    base: (): unknown[] => ['obligationTypes'],
    all: (clientId: unknown): unknown[] => ['obligationTypes', clientId],
  },
  /** Single obligation (active) for edit form in ObligationExtendedBlock */
  obligationDetail: (activeId: unknown, clientId: unknown): unknown[] => [
    'obligation',
    'detail',
    activeId,
    clientId,
  ],

  // Уведомления
  notifications: {
    base: (): unknown[] => ['notifications'],
    all: (clientId: unknown): unknown[] => ['notifications', 'all', clientId],
    paginated: (clientId: unknown, page: unknown, perPage: unknown): unknown[] => [
      'notifications',
      'paginated',
      clientId,
      page,
      perPage,
    ],
    unread: (clientId: unknown): unknown[] => ['notifications', 'unread', clientId],
  },

  // Клиенты (страница Клиенты для менеджера)
  clients: {
    base: (): unknown[] => ['clients'],
    list: (
      requestClientId: unknown,
      typeId: unknown,
      page: unknown,
      perPage: unknown
    ): unknown[] => ['clients', 'list', requestClientId, typeId, page, perPage],
  },

  // Чат (сообщения по чату, список чатов GET chats)
  chat: {
    base: (): unknown[] => ['chat'],
    /** Список чатов (контактов) для userId. */
    list: (userId: unknown): unknown[] => ['chat', 'list', userId],
    /** Сообщения чата: chatId, userId, take, lastId (для пагинации «назад»). */
    messages: (chatId: unknown, userId: unknown, take?: unknown, lastId?: unknown): unknown[] => [
      'chat',
      'messages',
      chatId,
      userId,
      take,
      lastId,
    ],
    /** Файл чата по file_path (кеш Blob для картинок/видео). */
    file: (chatId: unknown, userId: unknown, filePath: unknown): unknown[] => [
      'chat',
      'file',
      chatId,
      userId,
      filePath,
    ],
  },

  // Дивиденды по активу (список в форме акции)
  dividends: {
    base: (): unknown[] => ['dividends'],
    byActive: (clientId: unknown, activeId: unknown): unknown[] => [
      'dividends',
      clientId,
      activeId,
    ],
    /** Один дивиденд по id (для редактирования в модалке) */
    detail: (id: unknown): unknown[] => ['dividends', 'detail', id],
  },

  // Купоны по активу (список в форме облигации)
  coupons: {
    base: (): unknown[] => ['coupons'],
    byActive: (clientId: unknown, activeId: unknown): unknown[] => ['coupons', clientId, activeId],
    /** Один купон по id (для редактирования в модалке) */
    detail: (id: unknown): unknown[] => ['coupons', 'detail', id],
  },

  // Оценки актива (список в форме акции/облигации/ETF и т.д.)
  valuations: {
    base: (): unknown[] => ['valuations'],
    byActive: (clientId: unknown, activeId: unknown): unknown[] => [
      'valuations',
      clientId,
      activeId,
    ],
  },

  // Финансовая отчётность актива (TradingView — справочные данные, не зависят от сделок)
  financial: {
    /** Финданные по активу: clientId, activeId */
    byActive: (clientId: unknown, activeId: unknown): unknown[] => [
      'financial',
      clientId,
      activeId,
    ],
  },

  // Купоны/дивиденды по всем открытым позициям (active/index + item.*)
  activesPayouts: {
    base: (): unknown[] => ['activesPayouts'],
    all: (clientId: unknown): unknown[] => ['activesPayouts', 'all', clientId],
  },

  // Календарь выплат
  calendarPayments: {
    /** Базовый ключ для инвалидации всех запросов календаря выплат */
    base: (): unknown[] => ['calendarPayments'],
    /** Ключ для запроса всех выплат (без фильтра по датам) */
    all: (clientId: unknown): unknown[] => ['calendarPayments', 'all', clientId],
    /** Запрос выплат для календаря по датам */
    byDateRange: (clientId: unknown, fromDate: unknown, toDate: unknown): unknown[] => [
      'calendarPayments',
      clientId,
      fromDate,
      toDate,
    ],
  },
};
